<template>
  <div class="recurrence-selector">
    <b-form-group class="mt-2" label="">
      <div class="d-flex justify-content-start align-items-center">
        <span class="text-nowrap mr-2">Repeat every</span>
        <b-form-input
          v-model="frequencyNumber"
          class="frequency-number mr-2"
          type="number"
          number
          min="1"
          :max="maxFrequencyNumber"
          placeholder="1"
          :disabled="disabled"
          @blur="checkFrequencyNumber"
        ></b-form-input>
        <b-form-select
          v-model="frequencyUnit"
          :options="recurrencePeriods"
          class="frequency-unit"
          :disabled="disabled"
        ></b-form-select>
      </div>
    </b-form-group>
    <b-form-group
      v-if="frequencyUnit == 'Weeks'"
      class="mt-2"
      label="Repeat on"
      :state="formErrors.weekDay.length == 0 ? true : false"
      :invalid-feedback="formErrors.weekDay.join('. ')"
    >
      <week-days-picker align="left" v-model="weekDays" :disabled="disabled" />
    </b-form-group>
    <b-form-group
      v-if="frequencyUnit == 'Months'"
      class="mt-2"
      label="Repeat on"
    >
      <b-form-select
        v-model="monthRecurrence"
        :options="monthRecurrenceOptions"
        class="month-recurrence-input"
        :disabled="disabled"
      ></b-form-select>
    </b-form-group>
    <b-form-group
      class="mt-2"
      label="Ends"
      :state="formErrors.ends.length == 0 ? null : false"
      :invalid-feedback="formErrors.ends.join(', ')"
    >
      <div class="d-flex justify-content-start align-items-center">
        <b-form-radio
          v-model="endType"
          name="recurrence-end-type"
          class="recurrence-end-type"
          value="ByDate"
          :disabled="disabled"
          >On</b-form-radio
        >
        <date-picker
          :disabled="disabled || endType != 'ByDate'"
          class="recurrence-date-picker"
          v-model="endDate"
        />
      </div>
      <div class="d-flex justify-content-start align-items-center mt-2">
        <b-form-radio
          v-model="endType"
          name="recurrence-end-type"
          class="recurrence-end-type"
          value="ByOccurrences"
          :disabled="disabled"
          >After</b-form-radio
        >
        <b-form-input
          :disabled="disabled || endType != 'ByOccurrences'"
          v-model="occurrences"
          class="occurrences-input mr-2"
          type="number"
          number
          min="1"
          :max="maxOccurrences"
          placeholder="1"
          @blur="checkOcurrences"
        />
        <span class="text-nowrap">occurrences</span>
      </div>
    </b-form-group>
  </div>
</template>

<script>
import moment from "moment";
import DatePicker from "./DatePicker.vue";
import WeekDaysPicker from "./WeekDaysPicker";
import { formatDateOnlyMysql } from "../../helpers/date";

export default {
  name: "RecurrenceControl",
  components: { DatePicker, WeekDaysPicker },
  props: ["value", "startDate", "formErrors", "disabled", "maxOccurrences"],
  data() {
    return {
      recurrencePeriods: [
        {
          value: "Days",
          text: "days"
        },
        {
          value: "Weeks",
          text: "weeks"
        },
        {
          value: "Months",
          text: "months"
        },
        {
          value: "Years",
          text: "years"
        }
      ],
      selectedMonthOptionIndex: 0,
      maxFrequencyNumber: 365
    };
  },
  mounted() {
    this.$emit("input", {
      ...this.value,
      monthlyRepeat: this.monthRecurrenceOptions[0].value
    });
    if (this.value.monthlyRepeat) {
      const option = this.monthRecurrenceOptions.find(
        o => o.value == this.value.monthlyRepeat
      );
      this.selectedMonthOptionIndex = this.monthRecurrenceOptions.indexOf(
        option
      );
    }
  },
  methods: {
    checkFrequencyNumber() {
      if (
        !this.frequencyNumber ||
        this.frequencyNumber < 1 ||
        this.frequencyNumber > this.maxFrequencyNumber
      ) {
        this.$emit("input", { ...this.value, frequencyNumber: 1 });
      }
    },
    checkOcurrences() {
      if (!this.occurrences || this.occurrences < 1) {
        this.$emit("input", { ...this.value, occurrences: 1 });
      }
    },
    validateIsDateOrIsDateTime(date) {
      return (
        moment(date, "YYYY-MM-DDTHH:mm:ss", true).isValid() ||
        moment(date, "YYYY-MM-DD HH:mm:ss", true).isValid() ||
        moment(date, "YYYY-MM-DD", true).isValid()
      );
    }
  },
  computed: {
    frequencyNumber: {
      get() {
        return this.value.frequencyNumber;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, frequencyNumber: newValue });
      }
    },
    frequencyUnit: {
      get() {
        return this.value.frequencyUnit;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, frequencyUnit: newValue });
      }
    },
    weekDays: {
      get() {
        return [
          ...[this.value.sunday && "0"],
          ...[this.value.monday && "1"],
          ...[this.value.tuesday && "2"],
          ...[this.value.wednesday && "3"],
          ...[this.value.thursday && "4"],
          ...[this.value.friday && "5"],
          ...[this.value.saturday && "6"]
        ];
      },
      set(newValue) {
        this.$emit("input", {
          ...this.value,
          sunday: newValue.includes("0"),
          monday: newValue.includes("1"),
          tuesday: newValue.includes("2"),
          wednesday: newValue.includes("3"),
          thursday: newValue.includes("4"),
          friday: newValue.includes("5"),
          saturday: newValue.includes("6")
        });
      }
    },
    monthRecurrence: {
      get() {
        return this.monthRecurrenceOptions[this.selectedMonthOptionIndex].value;
      },
      set(newValue) {
        const option = this.monthRecurrenceOptions.find(
          o => o.value == newValue
        );
        this.selectedMonthOptionIndex = this.monthRecurrenceOptions.indexOf(
          option
        );
        this.$emit("input", { ...this.value, monthlyRepeat: newValue });
      }
    },
    endType: {
      get() {
        return this.value.endType;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, endType: newValue });
      }
    },
    endDate: {
      get() {
        return this.validateIsDateOrIsDateTime(this.value.endDate)
          ? formatDateOnlyMysql(this.value.endDate)
          : this.value.endDate;
      },
      set(newValue) {
        if (newValue) {
          this.$emit("input", {
            ...this.value,
            endDate: newValue
          });
        }
      }
    },
    occurrences: {
      get() {
        return this.value.occurrences;
      },
      set(newValue) {
        this.$emit("input", { ...this.value, occurrences: newValue });
      }
    },
    monthRecurrenceOptions() {
      const date = moment(this.startDate, "yyyy-MM-DD");
      const day = date.date();
      const weekDayName = date.format("dddd");
      const prefixes = ["first", "second", "third", "fourth", "fifth"];
      const weekLabel = prefixes[Math.floor(day / 7)];

      return [
        {
          value: "ByDayOfMonth",
          text: `Monthly, on day ${day}`
        },
        {
          value: "ByDayOfWeek",
          text: `Monthly, on the ${weekLabel} ${weekDayName}`
        }
      ];
    }
  },
  watch: {
    value(newValue) {
      this.selectedMonthOptionIndex = this.monthRecurrenceOptions
        .map(mRO => mRO.value)
        .indexOf(newValue.monthlyRepeat);
    }
  }
};
</script>

<style scoped>
.frequency-number {
  width: 90px;
}
.frequency-unit {
  width: 100px;
}
.recurrence-date-picker {
  width: 200px;
}
.recurrence-end-type {
  width: 100px;
}
.occurrences-input {
  width: 80px;
}
.month-recurrence-input {
  width: 300px;
}
</style>
